import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Card, Row, Col, Statistic, Typography, Spin, Button, Tooltip, Select } from 'antd';
import {
  WalletOutlined,
  DollarOutlined,
  SwapOutlined,
  LineChartOutlined,
  ShopOutlined,
  TagsOutlined,
  UserOutlined,
  InboxOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import Layout from '../../components/AdminLayout';
import { auth } from '@festpix/services';
import {
  getDashboardStats,
  getTodayTransactionsByHour,
  getSKUSalesData,
  DashboardStats,
  HourlyTransactionData,
  SKUSalesData,
  BoothOption,
} from '@festpix/services';
import { colors } from '@festpix/ui';

const { Title, Text } = Typography;

export default function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    inventoryValue: 0,
    boothCount: 0,
    skuCount: 0,
    vendorCount: 0,
    totalWallets: 0,
    totalBalance: 0,
    totalTransactions: 0,
    totalVolume: 0,
  });
  const [hourlyData, setHourlyData] = useState<HourlyTransactionData[]>([]);
  const [skuSalesData, setSKUSalesData] = useState<SKUSalesData[]>([]);
  const [booths, setBooths] = useState<BoothOption[]>([]);
  const [selectedBoothId, setSelectedBoothId] = useState<string | undefined>(undefined);

  const loadStats = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    }
    try {
      // getSKUSalesData now returns both sales data and booth list to avoid redundant fetching
      const [dashboardStats, transactionsByHour, skuSalesResult] = await Promise.all([
        getDashboardStats(),
        getTodayTransactionsByHour(),
        getSKUSalesData(selectedBoothId),
      ]);

      setStats(dashboardStats);
      setHourlyData(transactionsByHour);
      setSKUSalesData(skuSalesResult.salesData);
      setBooths(skuSalesResult.booths);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedBoothId]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        loadStats();
      }
    });
    return unsubscribe;
  }, [navigate, loadStats]);

  // Reload SKU sales data when booth filter changes
  useEffect(() => {
    if (!loading) {
      getSKUSalesData(selectedBoothId)
        .then((result) => setSKUSalesData(result.salesData))
        .catch(console.error);
    }
  }, [selectedBoothId, loading]);

  const handleRefresh = () => {
    loadStats(true);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  // Find max count for chart scaling
  const maxCount = Math.max(...hourlyData.map((d) => d.count), 1);

  // Find max sales count for SKU chart scaling
  const maxSalesCount = Math.max(...skuSalesData.map((d) => d.salesCount), 1);

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Painel Gerencial
        </Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {lastUpdated && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              Atualizado às {formatTime(lastUpdated)}
            </Text>
          )}
          <Tooltip title="Atualizar dados manualmente. Recomendado a cada 30s-1min durante o evento.">
            <Button
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={handleRefresh}
              loading={refreshing}
            >
              Atualizar
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Inventory & Operations KPIs */}
      <Title level={5} style={{ marginBottom: 16, color: colors.textSecondary }}>
        Inventário & Operações
      </Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ background: colors.cardBackground }}>
            <Statistic
              title="Valor do Inventário"
              value={stats.inventoryValue}
              precision={2}
              prefix={<InboxOutlined style={{ color: colors.warning }} />}
              valueStyle={{ color: colors.warning, fontSize: 20 }}
              suffix="R$"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ background: colors.cardBackground }}>
            <Statistic
              title="Estandes"
              value={stats.boothCount}
              prefix={<ShopOutlined style={{ color: colors.primary }} />}
              valueStyle={{ color: colors.primary, fontSize: 20 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ background: colors.cardBackground }}>
            <Statistic
              title="SKUs Cadastrados"
              value={stats.skuCount}
              prefix={<TagsOutlined style={{ color: colors.info }} />}
              valueStyle={{ color: colors.info, fontSize: 20 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ background: colors.cardBackground }}>
            <Statistic
              title="Vendedores"
              value={stats.vendorCount}
              prefix={<UserOutlined style={{ color: colors.success }} />}
              valueStyle={{ color: colors.success, fontSize: 20 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Financial KPIs */}
      <Title level={5} style={{ marginBottom: 16, color: colors.textSecondary }}>
        Financeiro
      </Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ background: colors.cardBackground }}>
            <Statistic
              title="Total de Carteiras"
              value={stats.totalWallets}
              prefix={<WalletOutlined style={{ color: colors.primary }} />}
              valueStyle={{ color: colors.primary, fontSize: 20 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ background: colors.cardBackground }}>
            <Statistic
              title="Saldo Total"
              value={stats.totalBalance}
              precision={2}
              prefix={<DollarOutlined style={{ color: colors.success }} />}
              valueStyle={{ color: colors.success, fontSize: 20 }}
              suffix="R$"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ background: colors.cardBackground }}>
            <Statistic
              title="Total de Transações"
              value={stats.totalTransactions}
              prefix={<SwapOutlined style={{ color: colors.info }} />}
              valueStyle={{ color: colors.info, fontSize: 20 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ background: colors.cardBackground }}>
            <Statistic
              title="Volume Total"
              value={stats.totalVolume}
              precision={2}
              prefix={<LineChartOutlined style={{ color: colors.warning }} />}
              valueStyle={{ color: colors.warning, fontSize: 20 }}
              suffix="R$"
            />
          </Card>
        </Col>
      </Row>

      {/* Transactions by Hour Chart */}
      <Card
        title={
          <span>
            <SwapOutlined style={{ marginRight: 8, color: colors.primary }} />
            Transações por Hora (Hoje)
          </span>
        }
        style={{ background: colors.cardBackground }}
      >
        <div style={{ height: 220, display: 'flex', alignItems: 'flex-end', gap: 2 }}>
          {hourlyData.map((data, index) => {
            const height = maxCount > 0 ? (data.count / maxCount) * 140 : 0;
            const currentHour = new Date().getHours();
            const isCurrentHour = index === currentHour;
            const hasVolume = data.volume > 0;

            return (
              <Tooltip
                key={data.hour}
                title={
                  <div>
                    <div>
                      <strong>{data.hour}</strong>
                    </div>
                    <div>{data.count} transações</div>
                    <div>R$ {data.volume.toFixed(2)}</div>
                  </div>
                }
              >
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    height: '100%',
                    justifyContent: 'flex-end',
                  }}
                >
                  {/* Value label on top of bar */}
                  {hasVolume && (
                    <Text
                      style={{
                        fontSize: 8,
                        color: isCurrentHour ? colors.primary : colors.textSecondary,
                        fontWeight: isCurrentHour ? 'bold' : 'normal',
                        marginBottom: 2,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {data.volume >= 1000
                        ? `${(data.volume / 1000).toFixed(1)}k`
                        : `${data.volume.toFixed(0)}`}
                    </Text>
                  )}
                  <div
                    style={{
                      width: '100%',
                      height: Math.max(height, data.count > 0 ? 4 : 0),
                      backgroundColor: isCurrentHour ? colors.primary : colors.primaryLight,
                      borderRadius: '2px 2px 0 0',
                      transition: 'height 0.3s ease',
                      minHeight: data.count > 0 ? 4 : 0,
                    }}
                  />
                  {/* Hour label for every hour */}
                  <Text
                    style={{
                      fontSize: 9,
                      color: isCurrentHour ? colors.primary : colors.textSecondary,
                      marginTop: 4,
                      fontWeight: isCurrentHour ? 'bold' : 'normal',
                    }}
                  >
                    {data.hour.split(':')[0]}
                  </Text>
                </div>
              </Tooltip>
            );
          })}
        </div>
        <div
          style={{
            marginTop: 16,
            padding: '12px 16px',
            background: colors.neutral100,
            borderRadius: 8,
            display: 'flex',
            justifyContent: 'space-around',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Total Hoje
            </Text>
            <div style={{ fontWeight: 'bold', color: colors.text }}>
              {hourlyData.reduce((sum, d) => sum + d.count, 0)} transações
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Volume Hoje
            </Text>
            <div style={{ fontWeight: 'bold', color: colors.success }}>
              R$ {hourlyData.reduce((sum, d) => sum + d.volume, 0).toFixed(2)}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Hora Pico
            </Text>
            <div style={{ fontWeight: 'bold', color: colors.primary }}>
              {hourlyData.reduce(
                (max, d, i, arr) => (d.count > arr[max].count ? i : max),
                0
              )}
              :00
            </div>
          </div>
        </div>
      </Card>

      {/* SKU Sales Chart */}
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <BarChartOutlined style={{ marginRight: 8, color: colors.success }} />
              Vendas por SKU
            </span>
            <Select
              placeholder="Todos os estandes"
              allowClear
              style={{ width: 200 }}
              value={selectedBoothId}
              onChange={setSelectedBoothId}
              options={[
                ...booths.map((booth) => ({
                  value: booth.id,
                  label: booth.name,
                })),
              ]}
            />
          </div>
        }
        style={{ background: colors.cardBackground, marginTop: 24 }}
      >
        {skuSalesData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: colors.textSecondary }}>
            Nenhuma venda registrada{selectedBoothId ? ' para este estande' : ''}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {skuSalesData.map((data) => {
              const barWidth = maxSalesCount > 0 ? (data.salesCount / maxSalesCount) * 100 : 0;

              return (
                <Tooltip
                  key={data.skuId}
                  title={
                    <div>
                      <div><strong>{data.skuName}</strong></div>
                      <div>Estande: {data.boothName}</div>
                      <div>{data.salesCount} unidades vendidas</div>
                    </div>
                  }
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 120, flexShrink: 0 }}>
                      <Text
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {data.shortName}
                      </Text>
                      {!selectedBoothId && (
                        <Text
                          type="secondary"
                          style={{
                            fontSize: 11,
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {data.boothName}
                        </Text>
                      )}
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: 24 }}>
                      <div
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${Math.max(barWidth, data.salesCount > 0 ? 2 : 0)}%`,
                          backgroundColor: colors.success,
                          borderRadius: 4,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    <div style={{ width: 60, textAlign: 'right', flexShrink: 0 }}>
                      <Text strong style={{ color: colors.success }}>
                        {data.salesCount}
                      </Text>
                    </div>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        )}
        {skuSalesData.length > 0 && (
          <div
            style={{
              marginTop: 16,
              padding: '12px 16px',
              background: colors.neutral100,
              borderRadius: 8,
              display: 'flex',
              justifyContent: 'space-around',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Total Vendas
              </Text>
              <div style={{ fontWeight: 'bold', color: colors.success }}>
                {skuSalesData.reduce((sum, d) => sum + d.salesCount, 0)} unidades
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                SKUs com Vendas
              </Text>
              <div style={{ fontWeight: 'bold', color: colors.text }}>
                {skuSalesData.filter((d) => d.salesCount > 0).length}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Mais Vendido
              </Text>
              <div style={{ fontWeight: 'bold', color: colors.primary }}>
                {skuSalesData[0]?.shortName || '-'}
              </div>
            </div>
          </div>
        )}
      </Card>
    </Layout>
  );
}
